import { useAuth } from "@clerk/nextjs";
import {
	Dialog,
	DialogBackdrop,
	DialogPanel,
	DialogTitle,
} from "@headlessui/react";
import { AnimatePresence, motion } from "framer-motion";
import { useRouter } from "next/compat/router";
import Link from "next/link";

import Button from "./Button";
import {
	useLazyIntercom,
	useStripeSubscription,
	useStripeSubscriptionFailed,
} from "~/hooks";
import { getPaymentErrorMessage } from "~/lib/stripe/getPaymentErrorMessage";

const PaymentDueDialog = () => {
	const { subscription } = useStripeSubscription();
	const { failedSubscriptionError } = useStripeSubscriptionFailed();
	const { show } = useLazyIntercom();
	const { actor } = useAuth();

	const router = useRouter();

	const isOpen =
		subscription &&
		!router?.asPath.startsWith("/membership") &&
		!router?.asPath.startsWith("/become-a-member") &&
		subscription.status === "past_due" &&
		!subscription.cancel_at_period_end &&
		!actor;

	const declineMessage = failedSubscriptionError
		? getPaymentErrorMessage({
				code: failedSubscriptionError.code,
				declineCode: failedSubscriptionError.decline_code,
				message: failedSubscriptionError.message,
			})
		: "Your last renewal payment did not go through. Update your card to keep unlimited access.";

	return (
		<AnimatePresence>
			{isOpen && (
				<Dialog open={isOpen} static onClose={() => {}}>
					<DialogBackdrop
						as={motion.div}
						initial={{ opacity: 0 }}
						animate={{ opacity: 1, transition: { duration: 0.3 } }}
						exit={{ opacity: 0, transition: { duration: 0.2 } }}
						className="fixed inset-0 z-50 bg-black/30 backdrop-blur dark:bg-black/40"
					/>

					<div className="fixed inset-0 z-50 flex items-center justify-center p-3">
						<DialogPanel
							as={motion.div}
							initial={{ opacity: 0, y: 15 }}
							animate={{
								opacity: 1,
								y: 0,
								transition: { duration: 0.2 },
							}}
							exit={{ opacity: 0, y: 15, transition: { duration: 0.15 } }}
							className="flex w-full max-w-md flex-col gap-3 phablet:gap-4 rounded-xl border bg-white phablet:px-8 px-4 phablet:py-6 py-5 dark:bg-slate-800"
						>
							<DialogTitle className="text-center font-semibold phablet:text-2xl text-xl">
								Payment Failed
							</DialogTitle>

							<p className="mb-2">{declineMessage}</p>

							<Link href="/membership/add-payment-method" className="w-full">
								<Button className="w-full">Update Payment Method</Button>
							</Link>

							<Link href="/membership" className="w-full">
								<Button variant="outlined" className="w-full">
									Manage Membership & Billing
								</Button>
							</Link>

							<p className="mt-3 mb-2 text-center">
								Need Assistance?{" "}
								<button
									className="font-medium text-sky-600 hover:underline dark:text-sky-300"
									onClick={() => show()}
								>
									Contact Support.
								</button>
							</p>
						</DialogPanel>
					</div>
				</Dialog>
			)}
		</AnimatePresence>
	);
};

export default PaymentDueDialog;
